import { useState } from 'react';
import type { ApproverDecision, TransitionRejection } from '@zevik/schemas';
import type { FindingEntry } from '../api/client';
import { namesFor, unnamedSentence } from '../review/gate';
import { Refused } from './credential/CredentialShell';

/**
 * Asking for the gate, and what the reviewer is told when it refuses
 * (docs/DECISIONS.md ZD-125 §3).
 *
 * The press is `POST /approve`, which carries nothing: it asks the state
 * machine to judge what has been recorded (ZD-061). A refusal is the guard's
 * own sentence, shown as it came, and — where the refusal is about findings —
 * those findings by title, each a link that selects it in the list. The
 * sentence says *what kind* of thing is outstanding; the titles say *which*.
 * Nothing here decides whether the gate would refuse: the control is offered
 * whatever has been recorded, and the API answers.
 *
 * A deferral refusal names the deferred findings and stops there. The round
 * that would discharge them is not on this screen (`Absences`).
 */
export function GateControl({
  findings,
  standing,
  onApprove,
  onSelect,
  unavailable,
}: {
  findings: readonly FindingEntry[];
  standing: ReadonlyMap<string, ApproverDecision>;
  /** Presses the gate; resolves to `null` through it, or to the refusal. */
  onApprove: () => Promise<TransitionRejection | null>;
  onSelect: (findingId: string) => void;
  /** Why the gate cannot be asked from here, where it cannot — e.g. reading a fixture. */
  unavailable: string | null;
}): React.JSX.Element {
  const [pressing, setPressing] = useState(false);
  const [refusal, setRefusal] = useState<TransitionRejection | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const press = async (): Promise<void> => {
    setRefusal(null);
    setFailure(null);
    setPressing(true);
    try {
      setRefusal(await onApprove());
    } catch (error) {
      setFailure(error instanceof Error ? error.message : String(error));
    } finally {
      setPressing(false);
    }
  };

  const names = refusal === null ? null : namesFor(refusal, findings, standing);
  const unnamed = names === null ? null : unnamedSentence(names.unnamed);

  return (
    <section className="gate" aria-label="The gate">
      <h3>Approve for reporting</h3>
      <p className="gate__lead">
        When every finding carries a decision, ask for the gate. The assessment is judged on what is
        recorded, and a refusal says what is still outstanding.
      </p>
      <div className="run__actions">
        <button
          type="button"
          className="submit"
          disabled={pressing || unavailable !== null}
          onClick={() => {
            void press();
          }}
        >
          {pressing ? 'Asking…' : 'Approve for reporting'}
        </button>
      </div>
      {unavailable === null ? null : <p className="notice">{unavailable}</p>}
      {failure === null ? null : <Refused message={failure} />}
      {refusal === null || names === null ? null : (
        <div className="gate__refusal" role="status">
          <p className="gate__reason">{refusal.reason}</p>
          {names.named.length === 0 ? null : (
            <ul className="gate__findings">
              {names.named.map((entry) => (
                <li key={entry.finding_id}>
                  <button
                    type="button"
                    className="submit--link"
                    onClick={() => {
                      onSelect(entry.finding_id);
                    }}
                  >
                    {entry.title}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {unnamed === null ? null : <p className="gate__unnamed">{unnamed}</p>}
        </div>
      )}
    </section>
  );
}
